import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Typography, IconButton, Tooltip, Button, Chip, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel, FormControlLabel, Checkbox,
  CircularProgress, Divider, Alert,
} from '@mui/material';
import {
  Close as CloseIcon, Save as SaveIcon, ContentCopy as CopyIcon,
  PlayArrow as TestIcon,
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import IntegrationService from '../../../../services/IntegrationService';
import FieldMappingEditor from './FieldMappingEditor';
import FlowStepDesigner from './FlowStepDesigner';

const DIRECTIONS = ['INBOUND', 'OUTBOUND'];
const DOMAINS = ['RECEIVING', 'INVENTORY', 'ORDER', 'SHIPPING', 'PUTAWAY', 'PICKING', 'MASTER_DATA'];
const OPERATIONS = ['CREATE', 'UPDATE', 'UPSERT', 'DELETE', 'ACTION'];

/**
 * Full-screen message type designer.
 *
 * Layout:
 * +--------------------------------------------------------------------+
 * | Header: metadata fields (code, name, direction, domain, entity)     |
 * +--------------------------------------------------------------------+
 * | Left: Source Schema     | Center: Field Mappings  | Right: Preview  |
 * | (Monaco JSON editor)    | (table editor)          | (test payload)  |
 * +--------------------------------------------------------------------+
 * | Bottom: Processing Flow (horizontal step pipeline)                  |
 * +--------------------------------------------------------------------+
 *
 * Props:
 *   messageType: existing message type object (null for new)
 *   onClose: () => void
 *   onSave: () => void (callback after save to refresh list)
 *   colors: theme colors
 */
const MessageTypeDesigner = ({ messageType, onClose, onSave, colors }) => {
  const isNew = !messageType?.id;

  // Metadata form
  const [form, setForm] = useState({
    messageTypeCode: '',
    messageTypeName: '',
    description: '',
    direction: 'INBOUND',
    domain: 'RECEIVING',
    targetEntityName: '',
    targetOperation: 'CREATE',
    validateOnReceive: true,
    isActive: true,
  });

  // Schema + mappings
  const [sourceSchema, setSourceSchema] = useState('');
  const [mappings, setMappings] = useState([]);
  const [defaultValues, setDefaultValues] = useState('{}');

  // Flow steps
  const [flowSteps, setFlowSteps] = useState([]);
  const [flowCode, setFlowCode] = useState('');

  // Preview
  const [testPayload, setTestPayload] = useState('{\n  \n}');
  const [previewResult, setPreviewResult] = useState('');

  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);

  // Load existing message type data
  useEffect(() => {
    if (messageType) {
      setForm({
        messageTypeCode: messageType.messageTypeCode || '',
        messageTypeName: messageType.messageTypeName || '',
        description: messageType.description || '',
        direction: messageType.direction || 'INBOUND',
        domain: messageType.domain || 'RECEIVING',
        targetEntityName: messageType.targetEntityName || '',
        targetOperation: messageType.targetOperation || 'CREATE',
        validateOnReceive: messageType.validateOnReceive !== false,
        isActive: messageType.isActive !== false,
      });
      setSourceSchema(messageType.sourceSchema || '');
      setMappings(messageType.fieldMapping?.mappings || []);
      setDefaultValues(
        messageType.defaultValues ? JSON.stringify(messageType.defaultValues, null, 2) : '{}'
      );

      // Load associated flow
      if (messageType.messageTypeCode) {
        loadFlow(messageType.messageTypeCode);
      }
    }
  }, [messageType]);

  const loadFlow = async (messageTypeCode) => {
    try {
      // Try to find a flow for this message type
      const flows = await IntegrationService.listFlows();
      const matchingFlow = flows?.find(f => f.messageTypeCode === messageTypeCode);
      if (matchingFlow) {
        const full = await IntegrationService.getFlow(matchingFlow.flowCode);
        setFlowCode(full.flowCode || '');
        setFlowSteps(full.steps || []);
      }
    } catch (err) {
      // No flow found — that's OK
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleMappingsChange = (newMappings) => {
    setMappings(newMappings);
    setDirty(true);
  };

  const handleStepsChange = (newSteps) => {
    setFlowSteps(newSteps);
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        sourceSchema: sourceSchema || null,
        fieldMapping: mappings.length > 0 ? { mappings } : null,
        defaultValues: defaultValues && defaultValues !== '{}' ? JSON.parse(defaultValues) : null,
      };

      if (isNew) {
        await IntegrationService.createMessageType(payload);
      } else {
        await IntegrationService.updateMessageType(messageType.id, payload);
      }

      // TODO: Save flow steps if changed (needs backend endpoint for flow CRUD)

      setDirty(false);
      if (onSave) onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestTransform = useCallback(() => {
    try {
      const input = JSON.parse(testPayload);
      // Client-side preview: apply field mappings to test payload
      const result = {};
      for (const mapping of mappings) {
        if (!mapping.source || !mapping.target) continue;
        const value = resolveJsonPath(input, mapping.source);
        if (value !== undefined && value !== null) {
          const transformed = applyTransform(value, mapping.transform);
          setNestedValue(result, mapping.target, transformed);
        }
      }
      setPreviewResult(JSON.stringify(result, null, 2));
    } catch (err) {
      setPreviewResult(`Error: ${err.message}`);
    }
  }, [testPayload, mappings]);

  const handleClose = () => {
    if (dirty && !window.confirm('You have unsaved changes. Discard?')) return;
    onClose();
  };

  const headerSx = {
    '& .MuiInputBase-input': { color: colors.text, fontSize: '0.85rem' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
    '& .MuiInputLabel-root': { color: colors.textSecondary },
  };

  return (
    <Dialog open fullScreen PaperProps={{ sx: { bgcolor: colors.background } }}>
      {/* Header bar */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2, py: 1, borderBottom: `1px solid ${colors.border}`, bgcolor: colors.surface,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ color: colors.text, fontWeight: 600 }}>
            {isNew ? 'New Message Type' : form.messageTypeCode}
          </Typography>
          <Chip label={form.direction} size="small"
            sx={{ bgcolor: form.direction === 'INBOUND' ? 'rgba(33,150,243,0.15)' : 'rgba(76,175,80,0.15)',
              color: form.direction === 'INBOUND' ? '#2196f3' : '#4caf50' }} />
          {dirty && <Chip label="modified" size="small" sx={{ bgcolor: 'rgba(255,152,0,0.15)', color: '#ff9800' }} />}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button size="small" variant="contained" startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={handleSave} disabled={saving || !form.messageTypeCode}
            sx={{ bgcolor: colors.success, textTransform: 'none', '&:hover': { bgcolor: colors.successDark || colors.success } }}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Tooltip title="Close">
            <IconButton onClick={handleClose} sx={{ color: colors.textSecondary }}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mx: 2, mt: 1 }}>{error}</Alert>}

      {/* Metadata fields row */}
      <Box sx={{
        display: 'flex', gap: 1.5, px: 2, py: 1.5,
        borderBottom: `1px solid ${colors.border}`, bgcolor: colors.surface,
        flexWrap: 'wrap', alignItems: 'center',
      }}>
        <TextField size="small" label="Code" value={form.messageTypeCode} disabled={!isNew}
          onChange={(e) => handleFormChange('messageTypeCode', e.target.value.toUpperCase())}
          sx={{ ...headerSx, width: 180 }} />
        <TextField size="small" label="Name" value={form.messageTypeName}
          onChange={(e) => handleFormChange('messageTypeName', e.target.value)}
          sx={{ ...headerSx, width: 200 }} />
        <FormControl size="small" sx={{ width: 130 }}>
          <InputLabel sx={{ color: colors.textSecondary }}>Direction</InputLabel>
          <Select value={form.direction} label="Direction"
            onChange={(e) => handleFormChange('direction', e.target.value)}
            sx={{ color: colors.text }}>
            {DIRECTIONS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 150 }}>
          <InputLabel sx={{ color: colors.textSecondary }}>Domain</InputLabel>
          <Select value={form.domain} label="Domain"
            onChange={(e) => handleFormChange('domain', e.target.value)}
            sx={{ color: colors.text }}>
            {DOMAINS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" label="Target Entity" value={form.targetEntityName}
          onChange={(e) => handleFormChange('targetEntityName', e.target.value)}
          placeholder="e.g., RECEIPT"
          sx={{ ...headerSx, width: 150 }} />
        <FormControl size="small" sx={{ width: 120 }}>
          <InputLabel sx={{ color: colors.textSecondary }}>Operation</InputLabel>
          <Select value={form.targetOperation} label="Operation"
            onChange={(e) => handleFormChange('targetOperation', e.target.value)}
            sx={{ color: colors.text }}>
            {OPERATIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Checkbox size="small" checked={form.validateOnReceive}
            onChange={(e) => handleFormChange('validateOnReceive', e.target.checked)}
            sx={{ color: colors.textSecondary, '&.Mui-checked': { color: colors.accent } }} />}
          label={<Typography variant="caption" sx={{ color: colors.textSecondary }}>Validate on receive</Typography>}
        />
      </Box>

      {/* Main content: 3 columns */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Left: Source Schema */}
        <Box sx={{ width: '30%', minWidth: 280, borderRight: `1px solid ${colors.border}`, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 1.5, py: 0.75, borderBottom: `1px solid ${colors.border}`, bgcolor: colors.surface }}>
            <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
              Source Schema (JSON Schema)
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Editor
              height="100%"
              language="json"
              theme="vs-dark"
              value={sourceSchema}
              onChange={(v) => { setSourceSchema(v || ''); setDirty(true); }}
              options={{ minimap: { enabled: false }, fontSize: 12, lineNumbers: 'on', scrollBeyondLastLine: false, wordWrap: 'on', automaticLayout: true }}
            />
          </Box>
        </Box>

        {/* Center: Field Mappings */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
            <FieldMappingEditor
              mappings={mappings}
              onChange={handleMappingsChange}
              colors={colors}
            />
          </Box>
        </Box>

        {/* Right: Preview */}
        <Box sx={{ width: '25%', minWidth: 250, borderLeft: `1px solid ${colors.border}`, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 1.5, py: 0.75, borderBottom: `1px solid ${colors.border}`, bgcolor: colors.surface, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
              Preview
            </Typography>
            <Button size="small" startIcon={<TestIcon />} onClick={handleTestTransform}
              sx={{ textTransform: 'none', color: colors.accent, fontSize: '0.7rem' }}>
              Test
            </Button>
          </Box>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flex: 1, borderBottom: `1px solid ${colors.border}` }}>
              <Box sx={{ px: 1, py: 0.5, bgcolor: colors.surface }}>
                <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem' }}>Sample Input</Typography>
              </Box>
              <Editor
                height="calc(100% - 24px)"
                language="json"
                theme="vs-dark"
                value={testPayload}
                onChange={(v) => setTestPayload(v || '{}')}
                options={{ minimap: { enabled: false }, fontSize: 11, lineNumbers: 'off', scrollBeyondLastLine: false, wordWrap: 'on', automaticLayout: true }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ px: 1, py: 0.5, bgcolor: colors.surface }}>
                <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem' }}>Transformed Output</Typography>
              </Box>
              <Editor
                height="calc(100% - 24px)"
                language="json"
                theme="vs-dark"
                value={previewResult}
                options={{ minimap: { enabled: false }, fontSize: 11, lineNumbers: 'off', readOnly: true, scrollBeyondLastLine: false, wordWrap: 'on', automaticLayout: true }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Bottom: Processing Flow */}
      <Box sx={{ borderTop: `1px solid ${colors.border}`, p: 1.5, bgcolor: colors.surface, maxHeight: 200, overflow: 'auto' }}>
        <FlowStepDesigner
          steps={flowSteps}
          onChange={handleStepsChange}
          colors={colors}
        />
      </Box>
    </Dialog>
  );
};

// ========== Client-side transform preview helpers ==========

function resolveJsonPath(obj, path) {
  const clean = path.startsWith('$.') ? path.substring(2) : path;
  const parts = clean.split('.');
  let current = obj;
  for (const part of parts) {
    if (current == null) return undefined;
    if (part.includes('[*]')) {
      const arrayField = part.replace('[*]', '');
      current = current[arrayField];
    } else {
      current = current[part];
    }
  }
  return current;
}

function applyTransform(value, transform) {
  if (!transform) return value;
  const str = String(value);
  if (transform.startsWith("prefix('")) {
    const prefix = transform.slice(8, -2);
    return prefix + str;
  }
  if (transform.startsWith("suffix('")) {
    const suffix = transform.slice(8, -2);
    return str + suffix;
  }
  if (transform === 'toInteger') return parseInt(str, 10) || 0;
  if (transform === 'toString') return str;
  if (transform === 'toUpperCase') return str.toUpperCase();
  if (transform === 'toLowerCase') return str.toLowerCase();
  return value;
}

function setNestedValue(obj, path, value) {
  const parts = path.split('.');
  let current = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    if (!current[parts[i]]) current[parts[i]] = {};
    current = current[parts[i]];
  }
  current[parts[parts.length - 1]] = value;
}

export default MessageTypeDesigner;
